/**
 * Mapping des plateformes sociales pour normalisation et affichage.
 *
 * Contrairement aux spécialités (taxonomie métier figée), la liste des plateformes
 * évolue dans le temps (nouvelles apps). `normalizePlatform` ne bascule donc jamais
 * une valeur non reconnue vers un bucket générique : elle retourne la valeur nettoyée,
 * pour qu'une plateforme inédite reste visible et filtrable dans l'UI.
 */

export type NormalizedPlatform = string;

export interface PlatformMapping {
  normalized: string;
  displayName: string;
  variants: string[];
}

/**
 * Liste canonique — union de toutes les plateformes déjà utilisées dans les
 * formulaires (public, admin) et l'édition créateur, pour ne rien perdre au passage.
 */
export const PLATFORMS_MAPPING: PlatformMapping[] = [
  { normalized: 'Instagram', displayName: '📸 Instagram', variants: ['instagram', 'insta', 'ig'] },
  { normalized: 'TikTok', displayName: '🎵 TikTok', variants: ['tiktok', 'tik tok', 'tt'] },
  { normalized: 'YouTube', displayName: '▶️ YouTube', variants: ['youtube', 'yt'] },
  { normalized: 'Facebook', displayName: '👥 Facebook', variants: ['facebook', 'fb'] },
  { normalized: 'LinkedIn', displayName: '💼 LinkedIn', variants: ['linkedin', 'li'] },
  { normalized: 'Snapchat', displayName: '👻 Snapchat', variants: ['snapchat', 'snap', 'sc'] },
  { normalized: 'Pinterest', displayName: '📌 Pinterest', variants: ['pinterest', 'pin'] },
  { normalized: 'Twitter/X', displayName: '𝕏 Twitter/X', variants: ['twitter/x', 'twitter x', 'twitterx', 'twitter', 'x'] },
  { normalized: 'Twitch', displayName: '🟣 Twitch', variants: ['twitch'] },
];

export const CANONICAL_PLATFORMS: Array<{ id: string; label: string }> = PLATFORMS_MAPPING.map(
  ({ normalized, displayName }) => ({ id: normalized, label: displayName })
);

function normalizeSearchTerm(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/['']/g, '')
    .replace(/[-_/]/g, ' ')
    .replace(/\s+/g, ' ');
}

const variantsToNormalizedMap = new Map<string, string>();
PLATFORMS_MAPPING.forEach(({ normalized, variants }) => {
  variantsToNormalizedMap.set(normalizeSearchTerm(normalized), normalized);
  variants.forEach(variant => variantsToNormalizedMap.set(normalizeSearchTerm(variant), normalized));
});

/**
 * Normalise une plateforme : casse/accents/espaces/tirets tolérés. Une valeur
 * non reconnue est retournée nettoyée (ex: "bereal" -> "Bereal") plutôt que
 * rattachée à un bucket générique, pour rester filtrable.
 */
export function normalizePlatform(input: string): NormalizedPlatform {
  const trimmed = typeof input === 'string' ? input.trim() : '';
  if (!trimmed) return 'Autre';

  const match = variantsToNormalizedMap.get(normalizeSearchTerm(trimmed));
  if (match) return match;

  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

export function getPlatformDisplayName(normalized: NormalizedPlatform): string {
  const match = PLATFORMS_MAPPING.find(p => p.normalized === normalized);
  return match ? match.displayName : normalized;
}

/**
 * Traite une liste de plateformes brutes et retourne les valeurs normalisées uniques.
 */
export function processInputPlatforms(inputs: string[]): NormalizedPlatform[] {
  const set = new Set<string>();
  inputs.forEach(input => {
    if (!input || typeof input !== 'string') return;
    set.add(normalizePlatform(input));
  });
  return Array.from(set).sort();
}
